import React, { useRef } from 'react';
import { toast } from 'sonner';
import {
  DownloadSimple,
  FileCsv,
  FileDoc,
  FilePdf,
  FileText,
  FileXls,
  Upload,
} from '@phosphor-icons/react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import api from '../../services/api';

const FILE_ICON_BY_EXT = {
  csv: <FileCsv size={24} className="text-green-600" />,
  xlsx: <FileXls size={24} className="text-emerald-600" />,
  xls: <FileXls size={24} className="text-emerald-600" />,
  pdf: <FilePdf size={24} className="text-red-600" />,
  docx: <FileDoc size={24} className="text-blue-600" />,
  doc: <FileDoc size={24} className="text-blue-600" />,
};

const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return FILE_ICON_BY_EXT[ext] || <FileText size={24} className="text-slate-600" />;
};

const FieldGroup = ({ label, color, fields, suffix = '' }) => (
  <div>
    <p className={`text-xs font-semibold ${color} mb-1`}>{label}</p>
    <div className="flex flex-wrap gap-1.5">
      {fields.map((f) => (
        <Badge key={f} variant="outline" className={`text-xs ${color === 'text-red-600' ? 'bg-red-50 border-red-200 text-red-700' : color === 'text-blue-600' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white text-slate-500'}`}>
          {f}{suffix}
        </Badge>
      ))}
    </div>
  </div>
);

/**
 * Step 1 of the import flow — file upload + template download + field
 * reference. Owns nothing; all state lives in the parent.
 */
const ImportUploader = ({ importType, fields, file, setFile, showAllFields, setShowAllFields }) => {
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/import/template/${importType}/download`, {
        responseType: 'blob',
      });
      // Wrap in a Blob with an explicit CSV MIME so Safari/Firefox don't try
      // to render the response inline.
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${importType}_import_template.csv`);
      // Inside a Radix Dialog, the focus trap can swallow the synthetic
      // click before the browser starts the download. Appending the link
      // to document.body (outside the modal) + a small async tick lets the
      // download dispatch before we revoke the blob URL.
      link.style.position = 'fixed';
      link.style.opacity = '0';
      document.body.appendChild(link);
      link.click();
      // Defer cleanup — revoking the URL too early cancels the download in
      // some Chromium/Edge versions.
      setTimeout(() => {
        if (link.parentNode) link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 1000);
      toast.success('Template downloaded — check your browser downloads');
    } catch (e) {
      console.error('[ImportUploader] template download failed', e);
      toast.error('Failed to download template');
    }
  };

  return (
    <div className="space-y-4">
      <div
        className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-indigo-500 transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
        data-testid="file-drop-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,.pdf,.docx,.doc"
          onChange={(e) => e.target.files[0] && setFile(e.target.files[0])}
          className="hidden"
          data-testid="file-input"
        />
        {file ? (
          <div className="flex items-center justify-center gap-3">
            {getFileIcon(file.name)}
            <div className="text-left">
              <p className="font-medium text-slate-700">{file.name}</p>
              <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
          </div>
        ) : (
          <>
            <Upload size={40} className="mx-auto mb-3 text-slate-400" />
            <p className="text-slate-600 font-medium">Click to upload or drag and drop</p>
            <p className="text-sm text-slate-400 mt-1">CSV, Excel, PDF, or Word — scanned documents are read with OCR</p>
          </>
        )}
      </div>

      <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg p-3">
        <div>
          <p className="text-sm font-medium text-indigo-800">Need a template?</p>
          <p className="text-xs text-indigo-600">Download a CSV with all column headers pre-filled</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadTemplate}
          className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
          data-testid="download-template-btn"
        >
          <DownloadSimple size={16} className="mr-1" /> Download Template
        </Button>
      </div>

      <div className="bg-slate-50 rounded-lg p-4">
        <p className="text-sm font-medium text-slate-700 mb-3">
          Recognized fields for {importType}s:
        </p>
        <div className="space-y-3">
          <FieldGroup label="Required" color="text-red-600" fields={fields.required} suffix=" *" />
          <FieldGroup label="Recommended" color="text-blue-600" fields={fields.recommended} />
          {showAllFields && (
            <FieldGroup label="Optional" color="text-slate-500" fields={fields.optional} />
          )}
          <button
            type="button"
            onClick={() => setShowAllFields(!showAllFields)}
            className="text-xs text-indigo-600 hover:underline"
            data-testid="toggle-optional-fields"
          >
            {showAllFields ? 'Hide optional fields' : `Show ${fields.optional.length} optional fields...`}
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          Column headers are case-insensitive. Common variations are auto-detected.
        </p>
      </div>
    </div>
  );
};

export default ImportUploader;
